import { Capacitor } from '@capacitor/core';
import api, { API_BASE } from '@/lib/api';
import { FirebaseBootstrap } from '@/lib/firebaseBootstrap';

const LOG = '[resqly:push]';

let cachedAudio = null;
let cachedKey = '';
let lastPlayedAt = 0;
let activeOfferId = null;
let lastConfig = null;
let lastNativeOfferId = null;

function configKey(cfg) {
  return `${cfg?.channel_id || ''}:${cfg?.sound || ''}:${cfg?.channel_version || ''}`;
}

export async function syncDispatchRingtone() {
  const { data } = await api.get('/providers/me/dispatch-config');
  lastConfig = data;
  console.info(LOG, 'sync: config', {
    channel_id: data.channel_id,
    sound: data.sound,
    using_default: data.using_default,
  });
  const key = configKey(data);
  const token = localStorage.getItem('resqly_token');

  if (Capacitor.isNativePlatform()) {
    try {
      await FirebaseBootstrap.syncDispatchRingtone({
        channelId: data.channel_id || 'resqly_incoming_requests',
        usingDefault: Boolean(data.using_default),
        downloadUrl: `${API_BASE}/providers/me/dispatch-config/ringtone`,
        authToken: token || '',
        soundName: data.sound || 'default',
      });
    } catch (err) {
      console.info(LOG, 'native ringtone channel sync failed', err?.message || err);
    }
  }

  if (data.using_default || !data.sound || data.sound === 'default') {
    cachedAudio = null;
    cachedKey = key;
    console.info(LOG, 'sync: using default sound — foreground cache cleared');
    return data;
  }

  if (cachedKey === key && cachedAudio) {
    console.info(LOG, 'sync: cache fresh');
    return data;
  }

  try {
    const response = await api.get('/providers/me/dispatch-config/ringtone', {
      responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(response.data);
    if (cachedAudio?.src?.startsWith('blob:')) {
      URL.revokeObjectURL(cachedAudio.src);
    }
    cachedAudio = new Audio(blobUrl);
    cachedAudio.preload = 'auto';
    cachedAudio.loop = true;
    cachedKey = key;
    console.info(LOG, 'sync: ringtone downloaded/cached', {
      bytes: response.data?.size,
      type: response.data?.type,
    });
  } catch (err) {
    console.info(LOG, 'ringtone download failed', err?.response?.status, err?.message || err);
  }
  return data;
}

/**
 * Start the native continuous alert (DispatchAlertService) for an offer.
 * Deduplicated per offer; safe no-op on web.
 */
function startNativeOfferAlert(offerId) {
  if (!Capacitor.isNativePlatform()) return;
  const id = typeof offerId === 'string' && offerId ? offerId : activeOfferId;
  if (!id || lastNativeOfferId === id) return;
  lastNativeOfferId = id;
  FirebaseBootstrap.startOfferAlert({
    channelId: lastConfig?.channel_id || 'resqly_incoming_requests',
    offerId: id,
    apiBase: API_BASE,
    authToken: localStorage.getItem('resqly_token') || '',
  })
    .then((res) => {
      console.info(LOG, 'native alert started', { offerId: id, res: res || null });
    })
    .catch((err) => {
      console.info(LOG, 'native alert failed', err?.message || err);
      lastNativeOfferId = null;
    });
}

async function startPlayback(reason) {
  try {
    cachedAudio.loop = true;
    cachedAudio.currentTime = 0;
    console.info(LOG, 'play: start', { reason, activeOfferId });
    await cachedAudio.play();
    console.info(LOG, 'play: started');
    return true;
  } catch (err) {
    console.info(LOG, 'play: rejected', err?.name, err?.message || err);
    return false;
  }
}

/**
 * Start (or keep) looping playback for an incoming offer.
 * If the cache is empty, resync once and retry before giving up.
 */
export async function playCachedDispatchRingtone(offerId) {
  if (!cachedAudio) {
    console.info(LOG, 'play: cache empty — resyncing once');
    try {
      await syncDispatchRingtone();
    } catch (err) {
      console.info(LOG, 'play: resync failed', err?.message || err);
    }
  }
  if (!cachedAudio) {
    console.info(LOG, 'play: no cached audio available');
    return false;
  }

  const now = Date.now();
  if (now - lastPlayedAt < 800) return true;

  lastPlayedAt = now;
  if (typeof offerId === 'string' && offerId) activeOfferId = offerId;

  startNativeOfferAlert(offerId);

  const started = await startPlayback('incoming-offer');
  if (!started) {
    // One self-healing retry: refresh cache, then attempt playback again.
    try {
      await syncDispatchRingtone();
    } catch (err) {
      console.info(LOG, 'play: retry resync failed', err?.message || err);
    }
    if (cachedAudio) await startPlayback('retry');
  }
  return started;
}

/** Update the offer the ringer is associated with; keep it ringing. */
export function setActiveDispatchOffer(offerId) {
  if (!offerId || activeOfferId === offerId) return;
  activeOfferId = offerId;
  console.info(LOG, 'active offer set', { activeOfferId });
  startNativeOfferAlert(offerId);
  if (cachedAudio && cachedAudio.paused) {
    lastPlayedAt = Date.now();
    startPlayback('poll-new-offer');
  }
}

/** Stop looping playback; keeps the blob cache for future offers. */
export function stopDispatchRingtone(reason) {
  if (activeOfferId !== null || (cachedAudio && !cachedAudio.paused)) {
    console.info(LOG, 'stop', { reason, activeOfferId });
  }
  activeOfferId = null;
  if (Capacitor.isNativePlatform()) {
    FirebaseBootstrap.stopOfferAlert().catch((err) => {
      console.info(LOG, 'native stop failed', err?.message || err);
    });
  }
  lastNativeOfferId = null;
  if (cachedAudio) {
    try {
      cachedAudio.pause();
      cachedAudio.currentTime = 0;
    } catch (err) {
      console.info(LOG, 'stop: error pausing audio', err?.message || err);
    }
  }
}
